import styled from "@emotion/styled";
import {
  AspectRatio,
  AspectRatios,
  ThemeTypes,
  PauseButton,
  Breakpoints,
  MediaSection,
  Spacings,
  SectionInner,
  Row,
  MainAxisAlignments,
  useVideoPlayerAutoplay,
} from "@polestar/component-warehouse-react";
import { SwitchableThemeProvider } from "@polestar/component-warehouse-react/components/theme-providers/ThemeProviders";
import { useRef, useEffect } from "react";

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ControlsWrapper = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  margin-bottom: ${Spacings.large};
`;

interface Props {
  readonly video: {
    controls: {
      theme: string;
      ariaLabels: { play: string; pause: string };
    };
    desktop: {
      url: string;
      mimeType: string;
    };
    tablet: {
      url: string;
      mimeType: string;
    };
    mobile: {
      url: string;
      mimeType: string;
    };
  };
}

const Video = ({ video }: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { autoPlay } = useVideoPlayerAutoplay(videoRef);

  useEffect(() => {
    // Workaround: React does not include the "muted" property on hydration, causing native autoplay functionality to not work properly
    // https://github.com/facebook/react/issues/10389
    if (videoRef.current) {
      videoRef.current.muted = true;
    }

    // The PauseButton does not play well with the native `autoPlay` attribute, and will show the wrong state randomly
    // Solution from: https://storybook.polestar.com/component-warehouse/?path=/docs/components-buttons-pausebutton--autoplay-hide-safari-low-power-mode-button
    autoPlay();
  }, [videoRef, autoPlay]);

  return (
    <MediaSection>
      <AspectRatio
        ratio={{
          desktop: AspectRatios.xxWide,
          tablet: AspectRatios.wide,
          mobile: AspectRatios.tall,
        }}
      >
        <StyledVideo
          ref={videoRef}
          loop
          playsInline
          muted
          data-autoplay="true"
          data-testid="video"
        >
          <source
            src={video.desktop.url}
            type={video.desktop.mimeType}
            media={`(min-width: ${Breakpoints.desktop}px)`}
            data-testid="video-source-desktop"
          />
          <source
            src={video.tablet.url}
            type={video.tablet.mimeType}
            media={`(min-width: ${Breakpoints.tablet}px)`}
            data-testid="video-source-tablet"
          />
          <source
            src={video.mobile.url}
            type={video.mobile.mimeType}
            media={`(min-width: ${Breakpoints.mobile}px)`}
            data-testid="video-source-mobile"
          />
        </StyledVideo>

        <SwitchableThemeProvider
          type={ThemeTypes[video.controls.theme as keyof typeof ThemeTypes]}
        >
          <ControlsWrapper>
            <SectionInner>
              <Row mainAxisAlignment={MainAxisAlignments.end}>
                <PauseButton
                  indicator
                  mediaRef={videoRef}
                  ariaLabels={video.controls.ariaLabels}
                />
              </Row>
            </SectionInner>
          </ControlsWrapper>
        </SwitchableThemeProvider>
      </AspectRatio>
    </MediaSection>
  );
};

export default Video;
